import { useEffect, useState, type ReactNode } from 'react';
import { STATION_LABEL, SAMPLE_TYPE_LABEL } from '@dru/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CatalogueHazard, CataloguePackage, CatalogueProtocol, CatalogueRestriction,
  CatalogueTest, CursorPage, Industry as IndustryRow,
  TestCategory,
  LoincHit,
} from '@dru/types';
import { TEST_CATEGORY_LABEL } from '@dru/types';
import { Plus } from 'lucide-react';
import {
  Button, DataTable, FilterBar, StatusBadge, Tabs, cn, TextField, Field, Alert, Select, SearchInput, InfoTip,
} from '@dru/ui';
import { LoincPicker } from '../components/LoincPicker';
import { api } from '../../../lib/api';
import { fetchAll } from '../../../lib/fetchAll';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { Suggestions } from './Suggestions';
import { ClinicalTemplates } from './ClinicalTemplates';
import { NotForYourRole } from './Audit';
import { ProtocolDialog } from '../components/CatalogueDialogs';
import { CatalogueState, plural } from '../components/RetireAction';

/** Offered only to a holder of the permission the API checks on the write. */
function useCan(permission: string) {
  return useAuth().permissions.has(permission);
}


/**
 * The clinical catalogue.
 *
 * Platform data: a hazard is a hazard and an LFT is an LFT. A company reads
 * this and cannot change it, because one customer editing a protocol would
 * change what every other customer is obliged to run.
 *
 * The screen is built around one rule that is easy to get wrong. A protocol's
 * battery is **versioned, never edited** — an examination snapshots the
 * protocol it was raised under, so editing a published version would silently
 * rewrite what a past examination was obliged to do. The UI offers "publish a
 * new version" and does not offer an edit, rather than offering an edit that
 * fails.
 */
// Packages first (31 Oct 2026): the two default packages decide what every
// examination runs, so they are what an administrator comes here to change.
const TABS = ['packages', 'tests', 'hazards', 'industries', 'protocols', 'restrictions', 'suggestions', 'templates'] as const;
type Tab = (typeof TABS)[number];

/**
 * Hidden for now (owner, 29 Oct 2026): job roles are not assigned to
 * customers' workers, so hazards and the protocols they pull in never reach
 * an examination — the two default packages decide the battery. The data and
 * the screens stay; take a tab out of this set to bring it back.
 */
const HIDDEN_TABS: ReadonlySet<Tab> = new Set<Tab>(['hazards', 'protocols']);

const TAB_LABEL: Record<Tab, string> = {
  tests: 'Tests',
  hazards: 'Hazards',
  industries: 'Industries',
  protocols: 'Protocols',
  packages: 'Packages',
  restrictions: 'Restrictions',
  // The lists under free-text clinical fields; they had their own sidebar link.
  suggestions: 'Suggestions',
  // Clinical shortcuts + smart templates (Requirement 4).
  templates: 'Clinical templates',
};

/**
 * "Show retired", as a filter every section shares.
 *
 * It used to be a lone checkbox in the page header, a long way from the table
 * it changed and 13px square on a phone. It is a filter on the rows, so it
 * sits with the other filters and gets a chip when it is on.
 */
const STATE_FILTER = {
  key: 'state',
  label: 'Showing',
  allLabel: 'In use only',
  options: [{ value: 'all', label: 'In use and retired' }],
};

interface SectionProps {
  includeInactive: boolean;
  filters: Record<string, string | undefined>;
  onFilters: (next: Record<string, string | undefined>) => void;
}

export function Masters() {
  const { permissions } = useAuth();
  if (!(['test.read', 'protocol.read', 'industry.read', 'package.read'].some((p) => permissions.has(p)))) {
    return <NotForYourRole title="Clinical masters" what="The clinical catalogue is maintained by platform administrators." />;
  }
  return <MastersPage />;
}

function MastersPage() {
  const [params, setParams] = useSearchParams();
  const { permissions } = useAuth();
  // Managing the suggestion lists is test.manage; a read-only reader of the
  // catalogue is not offered a tab that answers 403.
  const tabs = TABS.filter((t) => !HIDDEN_TABS.has(t) && (t !== 'suggestions' || holds(permissions, 'test.manage'))
    && (t !== 'templates' || holds(permissions, 'template.manage')));
  const asked = params.get('tab') ?? '';
  const tab = (tabs as readonly string[]).includes(asked) ? asked as Tab : tabs[0] ?? 'tests';
  const setTab = (t: string) => { const next = new URLSearchParams(params); next.set('tab', t); setParams(next, { replace: true }); };
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const includeInactive = filters.state === 'all';
  const section: SectionProps = { includeInactive, filters, onFilters: setFilters };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Clinical masters"
        hint="The clinical catalogue every company is examined against. A change here changes what employees are tested for, so entries are retired rather than deleted — past examinations keep what they used."
      />

      <Tabs
        tabs={tabs.map((t) => ({ key: t, label: TAB_LABEL[t] }))}
        active={tab}
        onChange={setTab}
      />

      {tab === 'tests' && <Tests {...section} />}
      {tab === 'hazards' && <Hazards {...section} />}
      {tab === 'industries' && <Industries {...section} />}
      {tab === 'protocols' && <Protocols {...section} />}
      {tab === 'packages' && <Packages {...section} />}
      {tab === 'restrictions' && <Restrictions {...section} />}
      {tab === 'suggestions' && <Suggestions embedded />}
      {tab === 'templates' && <ClinicalTemplates />}
    </div>
  );
}

/** Client-side text match for the short catalogue lists. */
function matches(term: string, ...fields: Array<string | null | undefined>) {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return fields.some((f) => (f ?? '').toLowerCase().includes(t));
}

const CATEGORIES: TestCategory[] = [
  'VITALS', 'VISION', 'AUDIOMETRY', 'SPIROMETRY', 'RADIOLOGY',
  'ECG', 'PATHOLOGY', 'PHYSICAL_EXAM',
];

const SAMPLE_TYPES = ['BLOOD_EDTA', 'BLOOD_SERUM', 'BLOOD_FLUORIDE', 'URINE', 'SPUTUM'];

const STATIONS = [
  'VITALS', 'VISION', 'AUDIOMETRY', 'SPIROMETRY',
  'RADIOLOGY', 'ECG', 'PHLEBOTOMY', 'PHYSICIAN',
];

const CATEGORY_OPTIONS = CATEGORIES.map((c) => ({ value: c, label: TEST_CATEGORY_LABEL[c] ?? c }));

const VALUE_LABEL: Record<string, string> = {
  NUMERIC: 'Number', ENUM: 'Fixed answers', TEXT: 'Text', BOOLEAN: 'Pass / fail',
};

function Tests({ includeInactive, filters, onFilters }: SectionProps) {
  const canManage = useCan('test.manage');
  const nav = useNavigate();
  // Followed to the end of the cursor. `/tests` used to be unbounded and is
  // now paged, so a single call would render the first 200 and look like the
  // whole catalogue.
  // Searched on the server, which already took `q`. Hundreds of rows in one
  // unfiltered table was the whole of this screen's navigation.
  const [search, setSearch] = useState('');
  const category = filters.category ?? 'ALL';
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  const tests = useQuery<CatalogueTest[]>(
    () => fetchAll<CatalogueTest>((cursor) => api.get<CursorPage<CatalogueTest>>(
      `/tests?limit=200&includeInactive=${includeInactive}`
      + (category !== 'ALL' ? `&category=${category}` : '')
      + (q ? `&q=${encodeURIComponent(q)}` : '')
      + (cursor ? `&cursor=${cursor}` : ''),
    )),
    [includeInactive, category, q],
  );
  // Fifty at a time: all 276 tests in one go made a tablet page sixteen
  // thousand pixels long. A new search or filter starts again from the top.
  const TEST_PAGE = 50;
  const [testsShown, setTestsShown] = useState(TEST_PAGE);
  useEffect(() => { setTestsShown(TEST_PAGE); }, [includeInactive, category, q, filters.inPackage]);
  const allTests = tests.data ?? [];
  const [adding, setAdding] = useState(false);

  // A panel name ("CBC", "lipid profile") is a package here, not a test.
  // Matched against the real package list, so the hint names a package that
  // exists and opens it, instead of describing one from memory.
  const packages = useQuery<CursorPage<CataloguePackage>>(() => api.get('/packages'), []);
  const panelMatches = (() => {
    const s = search.trim().toLowerCase();
    if (s.length < 2) return [];
    return (packages.data?.items ?? []).filter((p) =>
      p.name.toLowerCase().includes(s) || p.code.toLowerCase().includes(s)
      || (p.loincPanelCode ?? '').toLowerCase() === s).slice(0, 3);
  })();
  // Which default package runs a test: filtered here, over the full list
  // already loaded (fetchAll follows the cursor to the end).
  const inPackage = filters.inPackage;
  const visibleTests = allTests.filter((t) => {
    const ins = t.inDefaultPackages ?? [];
    if (inPackage === 'PRE_EMPLOYMENT' || inPackage === 'YEARLY') return ins.includes(inPackage);
    if (inPackage === 'NONE') return ins.length === 0;
    return true;
  });

  return (
    <div className="space-y-3">
      <Header
        title="Tests"
        hint="Every test, measurement and pathology parameter a package can include. Open one to change its reference bands, what an employee reads, or to retire it."
        onAdd={canManage ? () => setAdding(true) : undefined}
        addLabel="Add a test"
      />

      <FilterBar
        filters={[
          { key: 'category', label: 'Category', options: CATEGORY_OPTIONS },
          {
            key: 'inPackage', label: 'In package', allLabel: 'Any package',
            options: [
              { value: 'PRE_EMPLOYMENT', label: 'Pre-employment' },
              { value: 'YEARLY', label: 'Annual' },
              { value: 'NONE', label: 'Not in a default package' },
            ],
          },
          STATE_FILTER,
        ]}
        value={filters}
        onChange={onFilters}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search by name or code, e.g. PAT-HB"
          aria-label="Search tests"
          className="w-full sm:w-80"
        />
      </FilterBar>

      {/* A panel name is not a test here: it is a package of tests. Said so
          rather than showing an empty table for "CBC". */}
      {panelMatches.map((p) => (
        <div key={p.id} role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-info-600/25 bg-info-100 px-4 py-3 text-small text-info-600">
          <p className="min-w-0 flex-[1_1_18rem]">
            <span className="font-semibold">{p.name}</span> is a package of {plural(p.tests.length, 'test')}
            {p.defaultFor ? ` — the default for ${p.defaultFor === 'PRE_EMPLOYMENT' ? 'pre-employment' : 'annual and other'} examinations` : ''}.
          </p>
          <Button size="sm" variant="secondary" onClick={() => nav(`/admin/masters/packages/${p.id}`)}>
            Open package
          </Button>
        </div>
      ))}

      <DataTable<CatalogueTest>
        rows={visibleTests.slice(0, testsShown)}
        hasMore={visibleTests.length > testsShown}
        onLoadMore={() => setTestsShown((n) => n + TEST_PAGE)}
        loading={tests.loading}
        error={tests.error}
        onRetry={tests.reload}
        rowKey={(t) => t.id}
        onRowClick={(t) => nav(`/admin/masters/tests/${t.id}`)}
        total={visibleTests.length}
        empty={search || filters.category || inPackage
          ? { title: 'No test matches', hint: 'Try another name or code, or clear the category.' }
          : { title: 'No tests yet', hint: 'Add the first test, then add it to a package.' }}
        columns={[
          {
            key: 'code', header: 'Test', alwaysVisible: true,
            cell: (t) => (
              <div className="min-w-0">
                <div className="text-small font-medium text-neutral-900">{t.name}</div>
                <div className="font-mono text-caption text-neutral-500">{t.code}</div>
              </div>
            ),
          },
          {
            key: 'category', header: 'Category',
            cell: (t) => (
              <div className="text-caption text-neutral-700">
                {TEST_CATEGORY_LABEL[t.category] ?? t.category}
                <div className="text-neutral-500">
                  {t.station
                    ? `at ${STATION_LABEL[t.station as never] ?? t.station}`
                    // A non-pathology test with no station is capturable nowhere.
                    : <span className="font-medium text-overdue-600">No station — cannot be captured</span>}
                </div>
              </div>
            ),
          },
          {
            key: 'value', header: 'Result',
            cell: (t) => (
              <span className="text-caption text-neutral-700">
                {VALUE_LABEL[t.valueType] ?? t.valueType}{t.unit ? ` · ${t.unit}` : ''}
              </span>
            ),
          },
          {
            key: 'bands', header: 'Reference bands', align: 'right',
            cell: (t) => (t.valueType !== 'NUMERIC'
              ? <span className="text-caption text-neutral-500">Not numeric</span>
              : t.referenceRanges.length
                ? <span className="text-caption tabular-nums text-neutral-700">{plural(t.referenceRanges.length, 'band')}</span>
                // A numeric test with no band is never flagged high or low.
                : <span className="text-caption font-medium text-dueSoon-600">None — never flagged</span>),
          },
          {
            // Which of the two default packages run it (protocols are hidden
            // since 29 Oct 2026; the packages are what examinations run).
            key: 'used', header: 'In packages',
            cell: (t) => {
              const ins = t.inDefaultPackages ?? [];
              const names = [ins.includes('PRE_EMPLOYMENT') ? 'Pre-employment' : null, ins.includes('YEARLY') ? 'Annual' : null].filter(Boolean);
              return (
                <span className={cn('text-caption', names.length ? 'text-neutral-700' : 'text-neutral-500')}>
                  {names.length ? names.join(' · ') : 'Not in a package'}
                </span>
              );
            },
          },
          {
            key: 'state', header: 'State', mobileLabel: '',
            cell: (t) => <CatalogueState active={t.isActive} />,
          },
        ]}
      />

      {adding && (
        <AddTestDialog
          onClose={() => setAdding(false)}
          onCreated={(id) => { setAdding(false); nav(`/admin/masters/tests/${id}`); }}
        />
      )}
    </div>
  );
}

function AddTestDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [form, setForm] = useState({
    code: '', name: '', category: 'PATHOLOGY' as TestCategory,
    unit: '', valueType: 'NUMERIC', station: 'PHLEBOTOMY', consumerName: '',
    sampleType: 'BLOOD_SERUM', fastingHours: '',
  });
  const [loinc, setLoinc] = useState<LoincHit | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Choosing a term prefills the unit, once.
   *
   * Only when the field is still empty: LOINC's UCUM unit is explicitly an
   * EXAMPLE, and an administrator who has typed 'mmHg' means it. Overwriting
   * that with 'mm[Hg]' would silently change what the report prints.
   */
  function pickLoinc(hit: LoincHit | null) {
    setLoinc(hit);
    if (hit?.ucumUnit && !form.unit) setForm((f) => ({ ...f, unit: hit.ucumUnit! }));
    if (hit && !form.name.trim()) setForm((f) => ({ ...f, name: hit.longCommonName.slice(0, 160) }));
    // LOINC's consumer name is a starting point, not an answer: it writes
    // American spellings and laboratory phrasing. Prefilled so there is
    // something, editable because it is what a worker reads.
    if (hit?.consumerName && !form.consumerName) {
      setForm((f) => ({ ...f, consumerName: hit.consumerName! }));
    }
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      const created = await api.post<{ id: string }>('/tests', {
        code: form.code.toUpperCase(),
        name: form.name,
        category: form.category,
        ...(form.unit ? { unit: form.unit } : {}),
        valueType: form.valueType,
        ...(form.station ? { station: form.station } : {}),
        ...(form.consumerName.trim() ? { consumerName: form.consumerName.trim() } : {}),
        // Only a pathology test is drawn; the server refuses a tube on anything else.
        ...(form.category === 'PATHOLOGY' ? {
          sampleType: form.sampleType,
          ...(form.fastingHours.trim() ? { fastingHours: Number(form.fastingHours) } : {}),
        } : {}),
        ...(loinc ? {
          loincCode: loinc.loincCode,
          loincLongName: loinc.longCommonName,
          ...(loinc.ucumUnit ? { ucumUnit: loinc.ucumUnit } : {}),
        } : {}),
      }, crypto.randomUUID());
      // Land on the new test's page: reference bands (numeric) and the
      // allowed answers (fixed answers) are configured there, and a test
      // without them is either never flagged or cannot be entered at all.
      // This used to send a fixed-answer test to `/admin/tests/:id`, a route
      // that does not exist.
      onCreated(created.id);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  const drawn = form.category === 'PATHOLOGY';

  // Wide, and in sections with one spacing rule. Every control goes through
  // `Field`, so a label, its hint and its control stack the same way in every
  // column — a hint above one control and below its neighbour is what knocked
  // the two-column rows out of line. Hints that belong to a whole row sit
  // under the row for the same reason.
  return (
    <Dialog
      title="Add a test"
      wide
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}
            disabled={form.code.trim().length < 2 || form.name.trim().length < 2}>
            Add test
          </Button>
        </>
      )}
    >
      <div className="mt-5 space-y-6">
        <FormSection title="What it is">
          <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
            <TextField label="Code" required value={form.code} mono autoFocus placeholder="PAT-HB"
              onChange={(v) => setForm({ ...form, code: v.toUpperCase() })}
              hint="It cannot repeat."
              info="The code appears on packages, results and reports." />
            <TextField label="Name" required value={form.name} placeholder="Haemoglobin"
              onChange={(v) => setForm({ ...form, name: v })} />
          </div>
        </FormSection>

        <FormSection
          title="How it is recorded"
          info={drawn
            ? 'Pathology is drawn as a sample and resulted by the laboratory, with a second signature before release.'
            : undefined}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Category">
              {(p) => (
                <Select {...p} value={form.category}
                  onChange={(e) => {
                    const category = e.target.value as TestCategory;
                    // The station follows the category, because it nearly
                    // always matches; it stays editable for the exceptions.
                    setForm({ ...form, category, station: DEFAULT_STATION[category] ?? form.station });
                  }}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{TEST_CATEGORY_LABEL[c]}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Captured at">
              {(p) => (
                <Select {...p} value={form.station}
                  onChange={(e) => setForm({ ...form, station: e.target.value })}>
                  {STATIONS.map((s) => <option key={s} value={s}>{STATION_LABEL[s] ?? s}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Value">
              {(p) => (
                <Select {...p} value={form.valueType}
                  onChange={(e) => setForm({ ...form, valueType: e.target.value })}>
                  <option value="NUMERIC">Numeric — flagged against a band</option>
                  <option value="ENUM">Fixed answers — a chosen option</option>
                  <option value="TEXT">Text — a finding</option>
                  <option value="BOOLEAN">Pass / fail</option>
                </Select>
              )}
            </Field>
            {form.valueType === 'NUMERIC' && (
              <TextField label="Unit" value={form.unit} placeholder="g/dL, mmHg, dB"
                onChange={(v) => setForm({ ...form, unit: v })} />
            )}
          </div>
          {form.valueType === 'ENUM' && (
            <p className="rounded-btn border border-neutral-200 bg-neutral-50 px-3 py-2 text-caption text-neutral-600">
              The allowed answers are configured on the test&rsquo;s own page, which
              opens once this is saved. Until they exist nothing can be entered
              against this test.
            </p>
          )}
          {form.valueType === 'NUMERIC' && (
            <p className="text-caption text-neutral-500">
              Reference bands are set on the test&rsquo;s page, which opens once this is
              saved. Without one, a result is never flagged high or low.
            </p>
          )}
        </FormSection>

        {drawn && (
          <FormSection title="Sample">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Drawn into" info="The desk’s draw list is derived from the tube, one barcode per tube.">
                {(p) => (
                  <Select {...p} value={form.sampleType}
                    onChange={(e) => setForm({ ...form, sampleType: e.target.value })}>
                    {SAMPLE_TYPES.map((t) => <option key={t} value={t}>{SAMPLE_TYPE_LABEL[t]}</option>)}
                  </Select>
                )}
              </Field>
              <TextField label="Fasting (hours)" value={form.fastingHours} inputMode="numeric"
                placeholder="None"
                info="Fasting is printed on the employee’s appointment card; leave it blank when none is needed."
                onChange={(v) => setForm({ ...form, fastingHours: v.replace(/[^0-9]/g, '') })} />
            </div>
          </FormSection>
        )}

        <FormSection title="Mapping and what an employee reads">
          <LoincPicker value={loinc} onChange={pickLoinc} className="" />
          <TextField
            label="Name an employee sees"
            value={form.consumerName}
            placeholder={form.name.trim() || 'Same as the name above'}
            onChange={(v) => setForm({ ...form, consumerName: v })}
            hint="Leave blank to use the name above."
            info={'The employee app prints this. Left blank, it is the name above — never the LOINC long name, which is a clinician’s string.'}
          />
        </FormSection>

        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}

/** A titled group inside a form dialog. */
function FormSection({ title, info, children }: { title: string; info?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-1">
        <h3 className="text-caption font-medium uppercase tracking-wide text-neutral-500">{title}</h3>
        {info && <InfoTip label={`About ${title.toLowerCase()}`}>{info}</InfoTip>}
      </div>
      {children}
    </section>
  );
}

/** Where a new test in each category is captured, unless the author says otherwise. */
const DEFAULT_STATION: Partial<Record<TestCategory, string>> = {
  VITALS: 'VITALS', VISION: 'VISION', AUDIOMETRY: 'AUDIOMETRY', SPIROMETRY: 'SPIROMETRY',
  RADIOLOGY: 'RADIOLOGY', ECG: 'ECG', PATHOLOGY: 'PHLEBOTOMY', PHYSICAL_EXAM: 'PHYSICIAN',
};

function Hazards({ includeInactive, filters, onFilters }: SectionProps) {
  const canManage = useCan('hazard.manage');
  const nav = useNavigate();
  const hazards = useQuery<CursorPage<CatalogueHazard>>(
    () => api.get(`/hazards?includeInactive=${includeInactive}`),
    [includeInactive],
  );
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const rows = (hazards.data?.items ?? []).filter((h) => matches(search, h.name, h.code, h.description));

  return (
    <div className="space-y-3">
      <Header
        title="Hazards"
        hint="Workplace exposures. An employee whose job role carries a hazard is owed every live protocol linked to it."
        onAdd={canManage ? () => setAdding(true) : undefined}
        addLabel="Add a hazard"
      />
      <FilterBar filters={[STATE_FILTER]} value={filters} onChange={onFilters}>
        <SearchInput value={search} onValueChange={setSearch} placeholder="Search hazards"
          aria-label="Search hazards" className="w-full sm:w-72" />
      </FilterBar>
      <DataTable<CatalogueHazard>
        rows={rows}
        loading={hazards.loading}
        error={hazards.error}
        onRetry={hazards.reload}
        rowKey={(h) => h.id}
        onRowClick={(h) => nav(`/admin/masters/hazards/${h.id}`)}
        empty={search
          ? { title: 'No hazard matches', hint: 'Try another name or code.' }
          : { title: 'No hazards yet', hint: 'Add one, then link protocols to it.' }}
        columns={[
          {
            key: 'code', header: 'Hazard', alwaysVisible: true,
            cell: (h) => (
              <div className="min-w-0">
                <div className="text-small font-medium text-neutral-900">{h.name}</div>
                <div className="font-mono text-caption text-neutral-500">{h.code}</div>
              </div>
            ),
          },
          {
            key: 'desc', header: 'Where it occurs', hideOnMobile: true,
            cell: (h) => <span className="text-caption text-neutral-600">{h.description ?? 'Not described'}</span>,
          },
          {
            key: 'protocols', header: 'Protocols', align: 'right',
            cell: (h) => (h.protocolCount
              ? <span className="text-caption tabular-nums text-neutral-700">{h.protocolCount}</span>
              // A hazard with no protocol owes an exposed worker nothing.
              : <span className="text-caption font-medium text-overdue-600">None — owes nothing</span>),
          },
          {
            key: 'roles', header: 'Job roles', align: 'right',
            cell: (h) => <span className="text-caption tabular-nums text-neutral-700">{h.jobRoleCount}</span>,
          },
          {
            key: 'state', header: 'State', mobileLabel: '',
            cell: (h) => <CatalogueState active={h.isActive} />,
          },
        ]}
      />

      {adding && (
        <SimpleAdd
          title="Add a hazard"
          lead="A new hazard owes nobody anything until a protocol is linked to it and a company maps it to a job role."
          fields={[
            { key: 'code', label: 'Code', mono: true, hint: 'Letters, digits and hyphens. It cannot change later.' },
            { key: 'name', label: 'Name' },
            { key: 'description', label: 'Where it occurs', optional: true },
          ]}
          submitLabel="Add hazard"
          submit={(v) => api.post('/hazards', {
            code: v.code.toUpperCase(), name: v.name,
            ...(v.description ? { description: v.description } : {}),
          }, crypto.randomUUID())}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); hazards.reload(); }}
        />
      )}
    </div>
  );
}

function Protocols({ includeInactive, filters, onFilters }: SectionProps) {
  const canManage = useCan('protocol.manage');
  const nav = useNavigate();
  const protocols = useQuery<CursorPage<CatalogueProtocol>>(
    () => api.get(`/protocols?includeInactive=${includeInactive}`),
    [includeInactive],
  );
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const rows = (protocols.data?.items ?? []).filter((p) => matches(search, p.name, p.code, ...p.hazards.map((h) => h.name)));

  return (
    <div className="space-y-3">
      <Header
        title="Protocols"
        hint="What an employee exposed to a hazard is examined for, and how often. A protocol is never edited: open one to publish its next version."
        onAdd={canManage ? () => setCreating(true) : undefined}
        addLabel="Add a protocol"
      />
      <FilterBar
        filters={[{ ...STATE_FILTER, allLabel: 'Live versions only', options: [{ value: 'all', label: 'Live and superseded' }] }]}
        value={filters}
        onChange={onFilters}
      >
        <SearchInput value={search} onValueChange={setSearch} placeholder="Search by name, code or hazard"
          aria-label="Search protocols" className="w-full sm:w-72" />
      </FilterBar>
      <DataTable<CatalogueProtocol>
        rows={rows}
        loading={protocols.loading}
        error={protocols.error}
        onRetry={protocols.reload}
        rowKey={(p) => p.id}
        onRowClick={(p) => nav(`/admin/masters/protocols/${p.id}`)}
        empty={search
          ? { title: 'No protocol matches', hint: 'Try another name, code or hazard.' }
          : { title: 'No protocols yet', hint: 'Add one to say what an exposed employee is examined for.' }}
        columns={[
          {
            key: 'code', header: 'Protocol', alwaysVisible: true,
            cell: (p) => (
              <div className="min-w-0">
                <div className="text-small font-medium text-neutral-900">{p.name}</div>
                <div className="font-mono text-caption text-neutral-500">{p.code} · v{p.version}</div>
              </div>
            ),
          },
          {
            key: 'hazards', header: 'Applies to',
            cell: (p) => (
              <div className="flex flex-wrap gap-1">
                {p.hazards.map((h) => (
                  <StatusBadge key={h.id} tone="neutral">{h.name}</StatusBadge>
                ))}
                {!p.hazards.length && (
                  <span className="text-caption text-neutral-600">Every employee (baseline)</span>
                )}
              </div>
            ),
          },
          {
            key: 'tests', header: 'Tests', align: 'right',
            cell: (p) => (
              <span className="whitespace-nowrap text-caption tabular-nums text-neutral-700">
                {p.tests.length} · {p.tests.filter((t) => t.isMandatory).length} required
              </span>
            ),
          },
          {
            key: 'cadence', header: 'Repeat / valid', align: 'right',
            cell: (p) => (
              <span className="whitespace-nowrap text-caption tabular-nums text-neutral-700">
                {p.periodicityMonths} mo / {p.certificateValidityMonths} mo
              </span>
            ),
          },
          {
            key: 'state', header: 'State', mobileLabel: '',
            cell: (p) => (
              <StatusBadge tone={p.isActive ? 'covered' : 'neutral'}>
                {p.isActive ? 'Live' : 'Superseded'}
              </StatusBadge>
            ),
          },
        ]}
      />

      {creating && (
        <ProtocolDialog
          base={null}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); protocols.reload(); }}
        />
      )}
    </div>
  );
}

function Packages({ includeInactive, filters, onFilters }: SectionProps) {
  const nav = useNavigate();
  const [search, setSearch] = useState('');
  const packages = useQuery<CursorPage<CataloguePackage>>(
    () => api.get(`/packages?includeInactive=${includeInactive}`),
    [includeInactive],
  );
  const all = packages.data?.items ?? [];
  // The two defaults get their own cards; the table lists the rest.
  const defaults = (['PRE_EMPLOYMENT', 'YEARLY'] as const)
    .map((d) => all.find((p) => p.defaultFor === d && p.isActive))
    .filter((p): p is CataloguePackage => !!p);
  const rows = all.filter((p) => !(p.defaultFor && p.isActive))
    .filter((p) => matches(search, p.name, p.code, p.description))
    .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));

  return (
    <div className="space-y-3">
      {defaults.length > 0 && (
        <section aria-labelledby="default-packages-h" className="space-y-2">
          <h2 id="default-packages-h" className="text-h4 text-neutral-900">Default packages</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {defaults.map((p) => <DefaultPackageCard key={p.id} p={p} onOpen={() => nav(`/admin/masters/packages/${p.id}`)} />)}
          </div>
        </section>
      )}
      <Header
        title={defaults.length ? 'Other packages' : 'Packages'}
        hint="Two default packages serve every company: pre-employment examinations run the first, all others the annual one. Change their tests here for everyone, or on a company's page for that company only. Retired packages stay for the records that used them."
      />
      <FilterBar filters={[STATE_FILTER]} value={filters} onChange={onFilters}>
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search packages, e.g. CBC, Thyroid"
          aria-label="Search packages"
          className="w-full sm:w-80"
        />
      </FilterBar>
      <DataTable<CataloguePackage>
        rows={rows}
        loading={packages.loading}
        error={packages.error}
        onRetry={packages.reload}
        rowKey={(p) => p.id}
        onRowClick={(p) => nav(`/admin/masters/packages/${p.id}`)}
        empty={{
          title: search ? 'No package matches' : 'No other packages',
          hint: search ? 'Try another name or code.' : 'Every examination runs one of the two default packages above.',
        }}
        columns={[
          {
            key: 'code', header: 'Package', alwaysVisible: true,
            cell: (p) => (
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-small font-medium text-neutral-900">{p.name}</span>
                  {p.defaultFor && (
                    <StatusBadge tone="covered">{p.defaultFor === 'PRE_EMPLOYMENT' ? 'Default · pre-employment' : 'Default · annual and others'}</StatusBadge>
                  )}
                </div>
                <div className="font-mono text-caption text-neutral-500">{p.code}</div>
              </div>
            ),
          },
          {
            key: 'tests', header: 'Contains', align: 'right',
            cell: (p) => (
              <span className="whitespace-nowrap text-caption tabular-nums text-neutral-700">{plural(p.tests.length, 'test')}</span>
            ),
          },
          {
            key: 'state', header: 'State', mobileLabel: '',
            cell: (p) => <CatalogueState active={p.isActive} />,
          },
        ]}
      />
    </div>
  );
}

/** Pre-employment (PEC) or annual (AHC): the package every company runs. */
function DefaultPackageCard({ p, onOpen }: { p: CataloguePackage; onOpen: () => void }) {
  const pre = p.defaultFor === 'PRE_EMPLOYMENT';
  // What the battery covers, by test category, so "what is in it" is
  // answered without opening it.
  const byCategory = new Map<string, number>();
  for (const t of p.tests) byCategory.set(t.category, (byCategory.get(t.category) ?? 0) + 1);
  const cover = [...byCategory].sort((a, b) => b[1] - a[1])
    .map(([c, n]) => `${TEST_CATEGORY_LABEL[c as TestCategory] ?? c} ${n}`);
  return (
    <button
      type="button" onClick={onOpen}
      className="flex min-w-0 flex-col gap-2 rounded-card border border-neutral-200 bg-white p-4 text-left shadow-card transition-colors duration-hover hover:border-primary-300 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <span className="flex flex-wrap items-center gap-2">
        <StatusBadge tone="covered">{pre ? 'Pre-employment (PEC)' : 'Annual and other (AHC)'}</StatusBadge>
        <span className="font-mono text-caption text-neutral-500">{p.code}</span>
      </span>
      <span className="text-body font-semibold text-neutral-900">{p.name}</span>
      <span className="text-small text-neutral-600">
        {plural(p.tests.length, 'test')} · runs on {pre ? 'every pre-employment examination' : 'every annual and other examination'}, for all companies
      </span>
      {cover.length > 0 && <span className="text-caption text-neutral-500">{cover.join(' · ')}</span>}
      <span className="text-small font-medium text-primary-600">Open package →</span>
    </button>
  );
}

/**
 * Industries, and what each one implies.
 *
 * The mapping is what turns "we are a chemical plant" into a starting point
 * during onboarding. Hazards here are ADVISORY — they seed the picker during
 * company setup so nobody hunts through forty exposures — while what a worker
 * is actually owed still comes from their own job role. An office employee at
 * a chemical plant is not exposed to solvents, and an industry rule that
 * decided otherwise would over-screen them.
 */
function Industries({ includeInactive, filters, onFilters }: SectionProps) {
  const canManage = useCan('industry.manage');
  const nav = useNavigate();
  const industries = useQuery<CursorPage<IndustryRow>>(
    () => api.get(`/industries?includeInactive=${includeInactive}`),
    [includeInactive],
  );
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const rows = (industries.data?.items ?? []).filter((i) => matches(search, i.name, i.code, i.description));

  return (
    <div className="space-y-3">
      <Header
        title="Industries"
        hint="The list a company picks its industry from at registration. It is a label on the company; what its people are examined for comes from the two default packages."
        addLabel="Add an industry"
        onAdd={canManage ? () => setAdding(true) : undefined}
      />
      <FilterBar filters={[STATE_FILTER]} value={filters} onChange={onFilters}>
        <SearchInput value={search} onValueChange={setSearch} placeholder="Search industries"
          aria-label="Search industries" className="w-full sm:w-72" />
      </FilterBar>
      <DataTable<IndustryRow>
        rows={rows}
        loading={industries.loading}
        error={industries.error}
        onRetry={industries.reload}
        rowKey={(i) => i.id}
        onRowClick={(i) => nav(`/admin/masters/industries/${i.id}`)}
        empty={search
          ? { title: 'No industry matches', hint: 'Try another name or code.' }
          : { title: 'No industries yet', hint: 'Add one to drive onboarding recommendations.' }}
        columns={[
          {
            key: 'name', header: 'Industry', alwaysVisible: true,
            cell: (i) => (
              <div className="min-w-0">
                <div className="text-small font-medium text-neutral-900">{i.name}</div>
                <div className="font-mono text-caption text-neutral-500">{i.code}</div>
              </div>
            ),
          },
          {
            key: 'companies', header: 'Companies', align: 'right',
            cell: (i) => <span className="text-caption tabular-nums text-neutral-700">{i.companyCount}</span>,
          },
          {
            key: 'state', header: 'State', mobileLabel: '',
            cell: (i) => <CatalogueState active={i.isActive} />,
          },
        ]}
      />

      {adding && (
        <SimpleAdd
          title="Add an industry"
          lead="It appears in the registration form at once."
          fields={[
            { key: 'code', label: 'Code', mono: true, hint: 'Letters, digits and hyphens — CHEM, IT-CONSULT. It cannot change later.' },
            { key: 'name', label: 'Name', hint: 'As it should read in the registration form.' },
            { key: 'description', label: 'Description', optional: true, hint: 'One line, shown in the picker.' },
          ]}
          submitLabel="Add industry"
          submit={async (v) => {
            const created = await api.post<{ id: string }>('/industries', {
              code: v.code.toUpperCase(), name: v.name, ...(v.description ? { description: v.description } : {}),
            }, crypto.randomUUID());
            nav(`/admin/masters/industries/${created.id}`);
          }}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); industries.reload(); }}
        />
      )}
    </div>
  );
}

function Restrictions({ includeInactive, filters, onFilters }: SectionProps) {
  const canManage = useCan('restriction.manage');
  const restrictions = useQuery<CursorPage<CatalogueRestriction>>(
    () => api.get(`/restriction-codes?includeInactive=${includeInactive}`),
    [includeInactive],
  );
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const rows = (restrictions.data?.items ?? []).filter((r) => matches(search, r.code, r.label, r.publicLabel));

  return (
    <div className="space-y-3">
      <Header
        title="Restriction codes"
        hint="What a certifying physician can attach to a fit-with-restrictions decision: an instruction for the employer, and a shorter line a QR scan may show."
        onAdd={canManage ? () => setAdding(true) : undefined}
        addLabel="Add a restriction"
      />
      <FilterBar filters={[STATE_FILTER]} value={filters} onChange={onFilters}>
        <SearchInput value={search} onValueChange={setSearch} placeholder="Search restriction codes"
          aria-label="Search restriction codes" className="w-full sm:w-72" />
      </FilterBar>
      <DataTable<CatalogueRestriction>
        rows={rows}
        loading={restrictions.loading}
        error={restrictions.error}
        onRetry={restrictions.reload}
        rowKey={(r) => r.id}
        empty={search
          ? { title: 'No restriction code matches', hint: 'Try another code or wording.' }
          : { title: 'No restriction codes yet', hint: 'Add one so physicians can attach it to a certificate.' }}
        columns={[
          {
            key: 'label', header: 'What the employer is told', alwaysVisible: true,
            cell: (r) => (
              <div className="min-w-0">
                <div className="text-small font-medium text-neutral-900">{r.label}</div>
                <div className="font-mono text-caption text-neutral-500">{r.code}</div>
              </div>
            ),
          },
          {
            key: 'public', header: 'What a QR scan shows',
            cell: (r) => (
              // A different sentence on purpose, and D25 governs whether an
              // unauthenticated scan sees it at all.
              <span className="text-small text-neutral-700">{r.publicLabel}</span>
            ),
          },
          {
            key: 'used', header: 'Times applied', align: 'right',
            cell: (r) => (
              <span className="text-caption tabular-nums text-neutral-700">{r.timesApplied}</span>
            ),
          },
        ]}
      />

      {adding && (
        <SimpleAdd
          title="Add a restriction code"
          lead="Physicians can attach it to a certificate as soon as it is added. The two sentences are read by different people, so they are written separately."
          fields={[
            { key: 'code', label: 'Code', mono: true, hint: 'It cannot change later.' },
            {
              key: 'label', label: 'What the employer is told',
              hint: 'An instruction they can act on: "No work at height".',
            },
            {
              key: 'publicLabel', label: 'What a QR scan may say',
              hint: 'Seen by a gate guard with no login. Say the constraint, never the reason.',
            },
          ]}
          submitLabel="Add restriction code"
          submit={(v) => api.post('/restriction-codes', {
            code: v.code.toUpperCase(), label: v.label, publicLabel: v.publicLabel,
          }, crypto.randomUUID())}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); restrictions.reload(); }}
        />
      )}
    </div>
  );
}

function Header({ title, hint, onAdd, addLabel }: {
  title: string; hint: string; onAdd?: () => void; addLabel?: string;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 max-w-2xl flex-[1_1_18rem] items-center gap-1">
        <h2 className="text-h4 text-neutral-900">{title}</h2>
        <InfoTip label={`About ${title.toLowerCase()}`}>{hint}</InfoTip>
      </div>
      {onAdd && addLabel && (
        <Button onClick={onAdd} icon={<Plus className="h-4 w-4" aria-hidden />}>{addLabel}</Button>
      )}
    </div>
  );
}

function SimpleAdd({ title, lead, fields, submit, submitLabel, onClose, onDone }: {
  title: string;
  /** What adding it does, in one sentence, above the fields. */
  lead?: string;
  fields: Array<{ key: string; label: string; hint?: string; mono?: boolean; optional?: boolean }>;
  submit: (values: Record<string, string>) => Promise<unknown>;
  submitLabel: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(fields.map((f) => [f.key, ''])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = fields.every((f) => f.optional || values[f.key]?.trim());

  async function go() {
    setBusy(true); setError(null);
    try { await submit(values); onDone(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      title={title}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={go} loading={busy} disabled={!complete}>{submitLabel}</Button>
        </>
      )}
    >
      <div className="mt-3 space-y-4">
        {lead && <p className="text-small text-neutral-600">{lead}</p>}
        {fields.map((f, i) => (
          <TextField
            key={f.key}
            label={f.optional ? `${f.label} (optional)` : f.label}
            required={!f.optional}
            value={values[f.key] ?? ''}
            onChange={(v) => setValues({ ...values, [f.key]: v })}
            hint={f.hint}
            mono={f.mono}
            autoFocus={i === 0}
          />
        ))}
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}
